import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

interface RegisterProps {
  onNavigate: (page: string) => void;
}

function LogoMark() {
  return (
    <div className="flex items-center gap-[11px]">
      <span className="w-[13px] h-[13px] rounded-full grad-accent-simple" />
      <span className="font-display font-extrabold tracking-[0.16em] text-white text-[17px]">
        AUDIARO
      </span>
    </div>
  );
}

function BrandPanel() {
  return (
    <div
      className="relative hidden lg:flex flex-col justify-between overflow-hidden px-[60px] py-14"
      style={{
        background:
          'radial-gradient(120% 90% at 20% 0%, rgba(var(--a-rgb),.16) 0%, rgba(12,12,14,0) 55%), #0C0C0E',
      }}
    >
      <LogoMark />
      <div>
        <div
          className="font-display font-extrabold uppercase tracking-[0.16em] text-xs mb-5"
          style={{ color: 'var(--a-core)' }}
        >
          Text to studio audio
        </div>
        <h1 className="font-display font-extrabold text-white text-[52px] leading-[1.05] tracking-[-0.01em] m-0">
          Your words,
          <br />
          in a voice
          <br />
          <span className="text-grad">worth hearing.</span>
        </h1>
        <p className="text-mist-400 text-base leading-[1.7] max-w-[400px] mt-[22px] mb-0">
          Convert any script into natural, broadcast-ready audio with 120+ AI voices across Murf,
          ElevenLabs and OpenAI &mdash; in seconds.
        </p>
      </div>
      <div className="flex items-center gap-3.5 px-5 py-[18px] border border-ink-700 rounded-[14px] max-w-[440px] bg-[rgba(23,23,27,.6)]">
        <span className="w-11 h-11 rounded-full flex-none flex items-center justify-center font-display font-extrabold text-ink-950 grad-accent-simple">
          CV
        </span>
        <div>
          <p className="m-0 text-mist-200 text-sm leading-normal">
            &ldquo;We produce a week of podcast promos in one afternoon. Audiaro paid for itself day
            one.&rdquo;
          </p>
          <p className="mt-1.5 mb-0 text-mist-500 text-[12.5px]">
            Carlos V. &mdash; Funnel Master Consultant
          </p>
        </div>
      </div>
    </div>
  );
}

export function Register({ onNavigate }: RegisterProps) {
  const { register } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    const result = await register({
      email: formData.email,
      password: formData.password,
      first_name: formData.first_name || undefined,
      last_name: formData.last_name || undefined,
    });

    if (!result.success) {
      setError(result.error || 'Registration failed');
    } else {
      onNavigate('dashboard');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-[1.05fr_1fr] bg-ink-950">
      <BrandPanel />

      <div className="flex items-center justify-center p-6 sm:p-10 bg-ink-900">
        <div className="w-full max-w-[380px] fx">
          <div className="lg:hidden mb-10">
            <LogoMark />
          </div>

          <h2 className="font-display font-extrabold text-white text-[28px] mt-0 mb-1.5">
            Create your studio
          </h2>
          <p className="text-mist-400 text-sm mt-0 mb-7">Start with 10,000 free characters.</p>

          <form onSubmit={handleSubmit}>
            {error && (
              <div className="rounded-[11px] px-4 py-3 mb-[18px] text-status-danger text-[13px] bg-[rgba(216,54,47,.12)]">
                {error}
              </div>
            )}

            <div className="flex gap-3 mb-[18px]">
              <div className="flex-1">
                <label htmlFor="first_name" className="label">
                  First name
                </label>
                <input
                  id="first_name"
                  name="first_name"
                  type="text"
                  autoComplete="given-name"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  className="input"
                />
              </div>
              <div className="flex-1">
                <label htmlFor="last_name" className="label">
                  Last name
                </label>
                <input
                  id="last_name"
                  name="last_name"
                  type="text"
                  autoComplete="family-name"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  className="input"
                />
              </div>
            </div>

            <label htmlFor="email" className="label">
              Email
            </label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="input mb-[18px]"
              placeholder="you@example.com"
            />

            <label htmlFor="password" className="label">
              Password
            </label>
            <input
              id="password"
              name="password"
              type="password"
              autoComplete="new-password"
              required
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              className="input"
              placeholder="At least 8 characters"
            />
            <p className="text-mist-500 text-xs mt-1.5 mb-[18px]">
              Must contain uppercase, lowercase, and number
            </p>

            <label htmlFor="confirmPassword" className="label">
              Confirm password
            </label>
            <input
              id="confirmPassword"
              name="confirmPassword"
              type="password"
              autoComplete="new-password"
              required
              value={formData.confirmPassword}
              onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
              className="input mb-[18px]"
              placeholder="Confirm your password"
            />

            <label
              htmlFor="terms"
              className="flex items-start gap-2 text-mist-500 text-[13px] leading-normal cursor-pointer mb-6"
            >
              <input
                id="terms"
                name="terms"
                type="checkbox"
                required
                className="h-4 w-4 mt-0.5 flex-none rounded border-ink-600 bg-ink-950 accent-[var(--a-core)]"
              />
              <span>
                I agree to the{' '}
                <a href="#" className="no-underline" style={{ color: 'var(--a-link)' }}>
                  Terms of Service
                </a>{' '}
                and{' '}
                <a href="#" className="no-underline" style={{ color: 'var(--a-link)' }}>
                  Privacy Policy
                </a>
              </span>
            </label>

            <button type="submit" disabled={loading} className="btn-grad w-full py-3.5">
              {loading ? 'Creating account...' : 'Create account'}
            </button>
          </form>

          <p className="text-center text-mist-500 text-[13.5px] mt-[22px] mb-0">
            Have an account?{' '}
            <button
              onClick={() => onNavigate('login')}
              className="font-semibold bg-transparent border-0 p-0 cursor-pointer"
              style={{ color: 'var(--a-link)' }}
            >
              Sign in
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
